<template>
  <v-row class="row-mobile">
    <v-col class="mobile-header p-md-0">
      <v-col v-if="ressourceHeader" cols="12">
        <v-row class="row pt-4 pb-0">
          <v-col cols="10" class="pb-0 pt-2">
            <div class="titre-module pt-0 mb-1" v-html="titleHtml"></div>
          </v-col>
          <v-col cols="2" class="pt-1" style="height:45px;">
            <v-img v-if="canAdd" src='@/assets/SVG/add-gris.svg' @click="createItem" class="btn-add"></v-img>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12">
        <v-row class="row">
          <v-col cols="12" class="py-0">
            <search-filter-input :localStorageKey="localStorageKey"
                                :withFilterItemsThatHaveAlerts="true"
                                @inputUpdated="onSearchTextUpdated"></search-filter-input>
          </v-col>
          <v-col cols="2" class="pt-0">
            <div class="d-flex flex-row">
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-col>

    <v-col cols="12" class="px-2 py-1">
      <v-treeview :items="displayedTree"
                  expand-icon="fas fa-chevron-down"
                  dense
                  ref="tree"
                  activatable
                  :active.sync="treeViewActiveNodes"
                  return-object
                  :open.sync="treeViewOpenNodes"
                  class="module-treeview-rounded module-treeview py-2">
        <template v-if="prependIcon" v-slot:prepend="{ item }">
          <v-icon v-if="item.icon" :color="getNotifColor(item.notifType)">
            {{ item.icon }}
          </v-icon>
        </template>

        <template v-slot:label="{ item }">
          <v-row class="my-0 ms-0">
            <div v-if="item.picto" style="width: 30px" class="d-flex align-center">
              <v-img :src="pictoPath(item)" height="30" width="30"></v-img>
            </div>
            <div :class="getNotifClass(item.notifType) + ' ps-4 py-0 pe-0 col-10'" v-html="getHtmlNode(item)"></div>
          </v-row>
        </template>
      </v-treeview>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import SearchFilterInput from '@/framework/base/components/SearchFilterInput.vue';
import { TreeNode } from '@/framework/base/components/TreeNode';
import { isMobile } from '@/style/StyleHelpers';
import { getNotifPicto, getPicto } from '../helpers/Picto';
import Ressource from '@/framework/ressource/classes/Ressource';
import { NotifDefTypes } from '@/generated/classes/NotifDef';
import { defineComponent, PropType } from "vue";

export default defineComponent({
  components: {
    SearchFilterInput
  },
  props: {
    treeNodes: {
      type: Array as PropType<TreeNode[]>,
      required: true
    },
    prependIcon: {
      type: Boolean
    },
    localStorageKey: {
      type: String
    },
    getHtmlNode: {
      type: Function as PropType<(node: unknown) => string>
    },
    displayPdf: {
      type: Function as PropType<(node: unknown) => void>
    },
    nodeSelected: {
      type: Object as PropType<TreeNode | null>
    },
    canAdd: {
      type: Boolean
    },
    addLabel: {
      type: String
    },
    ressourceHeader: {
      type: Object as PropType<Ressource>
    }
  },
  created(): void {
    if (this.nodeSelected == null && this.openNodes.length == 0) {
      this.openNodes.push(this.treeNodes[0])
    }
  },
  data() {
    const searchNodes: TreeNode[] = [];
    const openNodes: TreeNode[] = [];
    const activeNode = null as TreeNode | null;

    return {
      isMobile: isMobile(),
      activeNode,
      openNodes,
      nodeMap: new Map<number, TreeNode>,
      searchNodes,
      searching: false
    };
  },
  computed: {
    treeViewActiveNodes: {
      get(): TreeNode[] {
        // by default select 1st node
        if (!this.activeNode && !this.isMobile) {
          this.setActiveNode(this.treeNodes[0]);
        }

        if (this.nodeSelected != null) {
          this.openNode(this.nodeSelected);
          this.setActiveNode(this.nodeSelected);
        }
        if (this.activeNode) {
          this.setActiveNodeUpdated(this.activeNode);
          return [this.activeNode];
        } else {
          return [];
        }
      },
      set(nodes: TreeNode[]) {
        if (nodes.length > 0) {
          this.activeNode = nodes[0];
          this.setActiveNodeUpdated(nodes[0]);
        }
      }
    },
    titleHtml(): string {
      let res = "";
      if (this.ressourceHeader) {
        const title = this.ressourceHeader.label.split(" ");
        res = title[0];
        if (title.length > 1) {
          res += " <span style='color:#E42724;''>" + title[1] + "</span>";
        }
      }
      return res;
    },
    treeViewOpenNodes: {
      get(): TreeNode[] {
        return this.openNodes;
      },
      set(nodes: TreeNode[]) {
        this.openNodes = nodes;
      }
    },
    displayedTree: {
      get(): TreeNode[] {
        this.nodeMap.clear();
        this.treeNodes.forEach((node) => {
          this.addToMap(node);
        })
        if (this.searching) {
          return this.searchNodes;
        } else {
          return this.treeNodes;
        }
      },
      set(nodes: TreeNode[]) {
        this.searchNodes = nodes;
      }
    }
  },

  methods: {
    setActiveNode(node: TreeNode) {
      this.activeNode = node;
    },
    pictoPath(node: TreeNode): string {
      const path = getNotifPicto(node.picto, node.notifDefType);
      return getPicto(path);
    },
    openNode(node: TreeNode) {
      const parent = this.nodeMap.get(node.parentId);
      if (parent && !this.openNodes.includes(parent)) {
        this.openNodes.push(parent);
        this.openNode(parent);
      }
    },
    setActiveNodeUpdated(node: TreeNode) {
      if (node) {
        this.$emit('activeNodeUpdated', node);
      }
    },
    onSearchTextUpdated(input: string): void {
      if (input) {
        this.searching = true;
        const item: TreeNode[] = [];
        this.nodeMap.forEach((node) => {
          if (node.searchable && node.searchable.toLocaleLowerCase().includes(input.toLowerCase())) {
            const parent = this.nodeMap.get(node.parentId);
            if (parent) {
              const found = item.find(a => a.id == parent.id);
              if (!found) {
                const cloneNode = TreeNode.fromJson(parent);
                cloneNode.children = [];
                cloneNode.children.push(node);
                item.push(cloneNode);
              } else if (!found.children.includes(node)) {
                found.children.push(node);
              }
            } else {
              item.push(node);
            }
            this.openNode(node);
          }
        });
        this.displayedTree = item;
      } else {
        this.searching = false;
        this.displayedTree = this.treeNodes;
        this.openNodes = [];
      }
    },
    addToMap(node: TreeNode) {
      this.nodeMap.set(node.id, node);
      node.children.forEach((child) => {
        // @ts-expect-error : generated method
        child.notifDefType = child.obj.notifType;
        if (child.notifDefType > NotifDefTypes.NONE && node.notifDefType != NotifDefTypes.ACTION) {
          // @ts-expect-error : generated method
          node.notifDefType = child.obj.notifType;
        }
        this.addToMap(child);
      })

      if (node.children.length == 0) {
        if (node.notifDefType > NotifDefTypes.NONE) {
          this.setNotif(node, node.notifDefType);
        }
      }
    },
    setNotif(node: TreeNode, notifDefType: NotifDefTypes) {
      const item = this.nodeMap.get(node.id);
      if (item && item.parentId >= 0) {
        const parent = this.nodeMap.get(item.parentId);
        if (parent && parent.notifDefType != NotifDefTypes.ACTION) {
          parent.notifDefType = notifDefType;
          this.setNotif(parent, notifDefType);
        }
      }
    },
    deleteObj(treeNode: TreeNode): void {
      this.$emit('deleteObj', treeNode);
    },
    getNotifColor(value: NotifDefTypes): string {
      if (value == NotifDefTypes.ACTION) {
        return 'error';
      } else if (value == NotifDefTypes.INFO) {
        return 'info';
      } else {
        return 'primary';
      }
    },
    getNotifClass(value: NotifDefTypes): string {
      if (value == NotifDefTypes.ACTION) {
        return 'text-red';
      } else if (value == NotifDefTypes.INFO) {
        return 'text-green';
      } else {
        return '';
      }
    },
    getNotifStyle(value: NotifDefTypes): string {
      if (value == NotifDefTypes.ACTION) {
        return 'action-bg';
      } else if (value == NotifDefTypes.INFO) {
        return 'info-bg';
      }
      return '';
    },
    createItem(): void {
      this.$emit('createItem');
    }
  }
})


</script>
